/**
 * StatsSection Component
 * Design: "Light with Dark Accents"
 * White stat cards on #F8F9FC background, blue/emerald icon boxes
 * Mobile: 2-column grid, cards auto-height, no text overflow
 */
import { useRef, useState, useEffect } from "react";
import { LineChart, Users, Target, Building2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

function useCountUp(target: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

const statValues = [20, 80, 10, 30];
const statIcons = [LineChart, Users, Target, Building2];
const statColors = ["blue", "emerald", "blue", "emerald"];
const statSublabels = {
  de: ["im Performance Marketing", "national & international", "vollständige Expertise", "öffentlicher Sektor"],
  en: ["in performance marketing", "national & international", "full expertise", "public sector"],
};

const iconColors: Record<string, string> = {
  blue: "bg-blue-50 text-blue-600 border border-blue-100",
  emerald: "bg-emerald-50 text-emerald-600 border border-emerald-100",
};
const valueColors: Record<string, string> = {
  blue: "text-blue-600",
  emerald: "text-emerald-600",
};

function StatItem({ value, suffix, label, sublabel, icon: Icon, color, index, started }: {
  value: number; suffix: string; label: string; sublabel: string;
  icon: React.ElementType; color: string; index: number; started: boolean;
}) {
  const count = useCountUp(value, 2200, started);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow flex items-start gap-3 sm:gap-5">
      <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 ${iconColors[color]}`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <div className={`font-sora font-bold text-3xl sm:text-4xl leading-none mb-1 ${valueColors[color]}`}>
          {count}<span>{suffix}</span>
        </div>
        <div className="font-sora font-semibold text-slate-800 text-sm leading-tight">{label}</div>
        <div className="text-slate-500 text-xs font-inter mt-0.5 leading-tight">{sublabel}</div>
      </div>
    </div>
  );
}

export default function StatsSection() {
  const ref = useRef<HTMLElement>(null);
  const [started, setStarted] = useState(false);
  const [inView, setInView] = useState(false);
  const { t, language } = useLanguage();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (inView) {
      const timer = setTimeout(() => setStarted(true), 200);
      return () => clearTimeout(timer);
    }
  }, [inView]);

  return (
    <section ref={ref} className="py-16 sm:py-20 relative" style={{ background: "#F8F9FC" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {t.stats.items.map((item, i) => {
            const numericValue = parseInt(item.value.replace("+", ""));
            return (
              <StatItem
                key={i}
                value={numericValue}
                suffix={item.value.includes("+") ? "+" : ""}
                label={item.label}
                sublabel={statSublabels[language][i] || item.desc}
                icon={statIcons[i]}
                color={statColors[i]}
                index={i}
                started={started}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
